import React, { useState } from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import {
  Dialog,
  Divider,
  Grid,
  IconButton,
  useMediaQuery,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import { Field, Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  GET_POSTCODE_REGEXP,
  PHONE_NUMBER_REGEX,
  PLZ_REGEX,
} from 'caterers/constants';
import { webToLeadData } from 'caterers/services/webToLeadData';
import Bugsnag from 'lib/bugsnag';
import { Mouseflow } from 'lib/mouseflow';
import Yup from 'lib/yup';
import FormikFormField from 'shared/components/FormikFormField';
import { SALESFORCE_LEAD_RECORD_TYPES_MAPPING } from 'shared/constants';
import { webToLeadRequest } from 'shared/services/webToLeadRequest';

const FORM_ID = 'claim-caterer-profile-form';

function ClaimCatererProfileForm({
  unsignedCaterer,
  isClaimCatererProfileFormOpened,
  closeClaimCatererProfileForm,
}) {
  const { t } = useTranslation('caterers');
  const handleClose = () => {
    closeClaimCatererProfileForm(false);
  };

  const [
    isClaimCatererProfileFormSubmitted,
    setIsClaimCatererProfileFormSubmitted,
  ] = useState(false);
  const [emailAddress, setEmailAddress] = useState(false);
  const mobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const {
    name: catererName,
    address: { street: address, city: cityWithPLZ },
  } = unsignedCaterer;

  const zipCode = cityWithPLZ?.match(GET_POSTCODE_REGEXP)[0];
  const city =
    unsignedCaterer.city?.fields.name || cityWithPLZ?.split(' ')[1] || '';

  const handleSubmit = async (values) => {
    try {
      Mouseflow.notify.formSubmitAttempt(FORM_ID);
      await webToLeadRequest(
        {
          ...values,
          record_type: SALESFORCE_LEAD_RECORD_TYPES_MAPPING['Partner Lead'],
        },
        webToLeadData
      );
      setIsClaimCatererProfileFormSubmitted(true);
      setEmailAddress(values.email);
      Mouseflow.notify.formSubmitSuccess(FORM_ID);
    } catch {
      Mouseflow.notify.formSubmitFailure(FORM_ID);
      Bugsnag.notify(
        new Error(
          `[Error trying to submit a webtolead form] with data: ${JSON.stringify(
            values
          )}`
        )
      );
    }
  };

  return (
    <>
      <Dialog
        open={isClaimCatererProfileFormOpened}
        onClose={handleClose}
        anchor="bottom"
        disableAutoFocus
        maxWidth="md"
      >
        <Box position="absolute" right={0} top={0} m={1}>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {isClaimCatererProfileFormSubmitted ? (
          <>
            <Box position="absolute" right={0} top={0} m={1}>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box p={3} mt={2} pb={4}>
              <StyledBox>
                <CheckCircleIcon fontSize="small" />
                <Typography variant="subtitle1" sm={{ ml: 1 }} color="inherit">
                  {t('claimCatererProfile.success')}
                </Typography>
              </StyledBox>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="claimCatererProfile.confirmation"
                  values={{ email: emailAddress }}
                  components={[<strong key="strong" />]}
                />
              </Typography>
            </Box>
            <Box textAlign="center" mb={3}>
              <Button
                size="lg"
                variant="secondary"
                role="button"
                aria-label="submit"
                id="contact-caterer-modal-continue"
                onClick={handleClose}
              >
                {t('claimCatererProfile.continue')}
              </Button>
            </Box>
          </>
        ) : (
          <Formik
            id={FORM_ID}
            initialValues={{
              caterer_name: catererName,
              first_name: '',
              last_name: '',
              zip_code: zipCode,
              city,
              address,
              email: '',
              phone: unsignedCaterer?.sourceJson.phone,
            }}
            validationSchema={Yup.object({
              caterer_name: Yup.string().required(t('formErrors.isRequired')),
              first_name: Yup.string().required(t('formErrors.isRequired')),
              last_name: Yup.string().required(t('formErrors.isRequired')),
              zip_code: Yup.string()
                .required(t('formErrors.isRequired'))
                .matches(PLZ_REGEX, t('formErrors.zipCode')),
              city: Yup.string().required(t('formErrors.isRequired')),
              address: Yup.string().required(t('formErrors.isRequired')),
              email: Yup.string()
                .required(t('formErrors.emailRequired'))
                .email(t('formErrors.correctFormatEmail')),
              phone: Yup.string()
                .required(t('formErrors.isRequired'))
                .matches(PHONE_NUMBER_REGEX, t('formErrors.phone')),
            })}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(formikBag) => {
              return (
                <Form>
                  <Box display="flex" flexDirection="column" flex={1}>
                    <Box display="flex" flexDirection="row" flex={0.8}>
                      <StyledFormContainer mobile={mobile}>
                        <Typography variant="h4" component="h1">
                          {t('claimCatererProfile.title')}
                        </Typography>
                        <Typography variant="body2" mt={1} mb={4}>
                          {t('claimCatererProfile.subtitle')}
                        </Typography>
                        <Divider />
                        <Box pb={2} mt={4}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Field
                                required
                                label={t('claimCatererProfile.catererName')}
                                placeholder={t(
                                  'claimCatererProfile.catererName'
                                )}
                                name="caterer_name"
                                component={FormikFormField}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Field
                                required
                                label={t('claimCatererProfile.firstName')}
                                placeholder={t('claimCatererProfile.firstName')}
                                name="first_name"
                                component={FormikFormField}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Field
                                required
                                label={t('claimCatererProfile.lastName')}
                                placeholder={t('claimCatererProfile.lastName')}
                                name="last_name"
                                component={FormikFormField}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                required
                                label={t('claimCatererProfile.address')}
                                placeholder={t('claimCatererProfile.address')}
                                name="address"
                                component={FormikFormField}
                              />
                            </Grid>
                            <Grid item md={3} xs={5}>
                              <Field
                                required
                                label={t('claimCatererProfile.zipCode')}
                                placeholder={t('claimCatererProfile.zipCode')}
                                name="zip_code"
                                component={FormikFormField}
                              />
                            </Grid>
                            <Grid item md={9} xs={7}>
                              <Field
                                required
                                label={t('claimCatererProfile.city')}
                                placeholder={t('claimCatererProfile.city')}
                                name="city"
                                component={FormikFormField}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Field
                                required
                                label={t('claimCatererProfile.email')}
                                placeholder={t('claimCatererProfile.email')}
                                name="email"
                                component={FormikFormField}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Field
                                required
                                label={t('claimCatererProfile.phone')}
                                placeholder={t('claimCatererProfile.phone')}
                                name="phone"
                                component={FormikFormField}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </StyledFormContainer>
                      {!mobile && <StyledSideImage />}
                    </Box>
                    <StyledSubmitButtonContainer>
                      <Button
                        disabled={!formikBag.dirty}
                        size="lg"
                        variant="primary"
                        type="submit"
                        role="button"
                        aria-label="submit"
                        id="contact-caterer-modal-submit"
                      >
                        {t('claimCatererProfile.submit')}
                      </Button>
                    </StyledSubmitButtonContainer>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        )}
      </Dialog>
    </>
  );
}

const StyledBox = styled.div`
  color: ${(props) => props.theme.palette.secondary.light};
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => props.theme.spacing(2)}px;

  ${(props) => props.theme.breakpoints.down('xs')} {
    flex-direction: column;
    align-items: start;
  }
`;

const StyledFormContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: ${(props) => (props.mobile ? 1 : 0.7)};
  padding-top: ${(props) => props.theme.spacing(3)}px;
  max-height: 80vh;
  overflow-y: auto;
  padding-left: ${(props) => props.theme.spacing(props.mobile ? 3 : 7)}px;
  padding-right: ${(props) => props.theme.spacing(props.mobile ? 3 : 11.5)}px;
`;

const StyledSideImage = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex: 0.3;
  background-image: url('/static/images/caterer/claim_caterer_profile_modal_background.webp');
  background-size: cover;
  background-position: center;
`;

const StyledSubmitButtonContainer = styled(Box)`
  display: flex;
  flex: 0.2;
  justify-content: center;
  align-items: center;
  background-color: white;
  box-shadow: 0 4px 40px rgba(62, 41, 29, 0.08);
  padding-top: ${(props) => props.theme.spacing(1)}px;
  padding-bottom: ${(props) => props.theme.spacing(1)}px;
`;

export default ClaimCatererProfileForm;
